import { CreateCallDataSourceDto, UpdateCallDataSourceDto } from "@dialogics/shared/dto/types"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { useProfileQuery } from "../auth/service"
import {
  useAddCallDataSourceMutation,
  useResetSampleFeaturesMutation,
  useSelectDebugSampleMutation,
  useUpdateCallDataSourceMutation,
} from "./service"

export function EditDataSourceModal({
  dataSource,
  organizationId,
  onClose,
  isOpen,
}: {
  dataSource: (CreateCallDataSourceDto & { id: number }) | null
  organizationId?: number
  onClose: () => void
  isOpen: boolean
}) {
  const { data: profile } = useProfileQuery()
  const [addDataSource] = useAddCallDataSourceMutation()
  const [updateDataSource] = useUpdateCallDataSourceMutation()
  const [selectDebugSample, { isLoading: isSelectingSample }] = useSelectDebugSampleMutation()
  const [resetSampleFeatures, { isLoading: isResetting }] = useResetSampleFeaturesMutation()
  const [sampleCount, setSampleCount] = useState(50)

  const { register, handleSubmit } = useForm<
    Omit<CreateCallDataSourceDto, "config" | "vars"> & {
      config: string
      vars: string
    }
  >({
    defaultValues: {
      ...dataSource,
      is_active: dataSource?.is_active ?? true,
      config: JSON.stringify(dataSource?.config ?? {}, null, 2),
      vars: JSON.stringify(dataSource?.vars ?? {}, null, 2),
    },
  })

  const onSubmit = (
    data: Omit<CreateCallDataSourceDto, "config" | "vars"> & {
      config: string
      vars: string
    },
  ) => {
    const payload = {
      ...data,
      config: data.config ? JSON.parse(data.config) : null,
      vars: data.vars ? JSON.parse(data.vars) : null,
    }
    if (dataSource) {
      updateDataSource({
        id: dataSource.id,
        ...(payload as UpdateCallDataSourceDto),
      })
    } else {
      addDataSource({
        ...payload,
        organization_id: organizationId ?? profile!.organization_id,
      } as CreateCallDataSourceDto)
    }
    onClose()
  }

  const handleSelectSample = async () => {
    if (!dataSource) return
    try {
      const result = await selectDebugSample({ id: dataSource.id, count: sampleCount }).unwrap()
      toast.success(`Выбрано ${result.count} записей в тестовую выборку`)
    } catch {
      toast.error("Ошибка при формировании тестовой выборки")
    }
  }

  const handleResetFeatures = async () => {
    if (!dataSource) return
    const confirmed = window.confirm(
      "Вы уверены? Это удалит все оценки фактов и типов звонков для тестовой выборки. Действие необратимо.",
    )
    if (!confirmed) return
    try {
      const result = await resetSampleFeatures(dataSource.id).unwrap()
      toast.success(`Удалено ${result.deleted} оценок`)
    } catch {
      toast.error("Ошибка при сбросе оценок")
    }
  }

  return (
    <dialog id="data_source_modal" className="modal" open={isOpen}>
      <div className="modal-box w-11/12 max-w-2xl">
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <h2 className="text-xl font-bold">{dataSource ? "Редактировать" : "Добавить"} источник данных</h2>

          <fieldset className="fieldset">
            <legend className="fieldset-legend">Тип</legend>
            <input {...register("type")} className="input w-full" />
          </fieldset>

          <fieldset className="fieldset">
            <legend className="fieldset-legend">Конфигурация</legend>
            <textarea {...register("config")} className="textarea h-32 w-full font-mono" />
          </fieldset>

          <fieldset className="fieldset">
            <legend className="fieldset-legend">Переменные</legend>
            <textarea {...register("vars")} className="textarea h-32 w-full font-mono" />
          </fieldset>

          <fieldset className="fieldset">
            <legend className="fieldset-legend">Активен</legend>
            <input type="checkbox" {...register("is_active")} className="toggle mt-1" />
          </fieldset>

          <div className="modal-action">
            <button type="button" className="btn" onClick={onClose}>
              Отмена
            </button>
            <button type="submit" className="btn btn-primary">
              Сохранить
            </button>
          </div>
        </form>

        {dataSource && (
          <>
            <div className="divider">Тестовая выборка</div>

            <div className="flex flex-col gap-4">
              <fieldset className="fieldset">
                <legend className="fieldset-legend">Количество записей в выборке</legend>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    className="input w-32"
                    min={1}
                    max={10000}
                    value={sampleCount}
                    onChange={e => setSampleCount(Number(e.target.value))}
                  />
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={handleSelectSample}
                    disabled={isSelectingSample}
                  >
                    {isSelectingSample && <span className="loading loading-spinner loading-sm" />}
                    Выбрать тестовые записи
                  </button>
                </div>
              </fieldset>

              <fieldset className="fieldset">
                <legend className="fieldset-legend">Сброс оценок</legend>
                <button
                  type="button"
                  className="btn btn-error btn-outline"
                  onClick={handleResetFeatures}
                  disabled={isResetting}
                >
                  {isResetting && <span className="loading loading-spinner loading-sm" />}
                  Перерасчитать оценку звонков
                </button>
              </fieldset>
            </div>
          </>
        )}
      </div>
      <form method="dialog" className="modal-backdrop" onClick={onClose}>
        <button>закрыть</button>
      </form>
    </dialog>
  )
}
